import { FileFormat } from '@fabric-ui/components/Table';
import { DwQueryRequest } from '../CustomDashboards/types';
import { MappingType } from '../Sources/Mapping/types';

export const TARGET_MATCHING_TYPE_ENUM = { ALWAYS: 'ALWAYS', NEVER: 'NEVER', CONDITION: 'CONDITION' };

export type SchemaField = {
  systemName: string;
  displayName: string;
  inputType: string;
  availableOptions: string[];
  required: boolean;
  fieldCategory: string;
  integrationOriginalField: {
    name: string;
    systemName: string;
    inputType: string;
    itemsType: string;
    customFieldType: string;
  };
};

export type OutegrationConfig = Record<string, string | string[] | boolean>;

export type CopyMappingDetails = {
  mapping: TMapping;
  integrationType?: string;
  projectionName?: string;
  staticSchemaType?: string;
};

export type TMapping = {
  id?: string;
  syncComments: boolean;
  syncCommentsTriggers: TriggerField[];
  createMapping: {
    fieldMapping: FieldMapping[];
  };
  createMappingEnabled: boolean;
  syncMapping: {
    fieldMapping: FieldMapping[];
  };
  syncMappingEnabled: boolean;
  webhookMapping: {
    fieldMapping: FieldMapping[];
  };
  attachmentOnCreate?: IntegrationAttachment;
  attachmentOnSync?: IntegrationAttachment;
  mappingProjection?: string;
};

export interface IntegrationAttachment {
  fileName: string;
  format: FileFormat;
  query: DwQueryRequest;
  triggerFields: TriggerField[];
  triggerOnFieldsChange: boolean;
}

export enum TriggerType {
  VALUE = 'VALUE',
  EMPTY = 'EMPTY',
  ANY = 'ANY',
}

export enum BulkOutegrationRunType {
  DID_NOT_RUN = 'DID_NOT_RUN',
  FAILED = 'FAILED',
  SUCCESSFUL = 'SUCCESSFUL',
}

export interface TriggerField {
  field?: string;
  from: { value: null | string; type: TriggerType };
  to: { value: null | string; type: TriggerType };
}

export enum BulkPublishingMode {
  Legacy = 'LEGACY',
  Parallel = 'PARALLEL_PARTITIONS',
}

export enum BulkPublishingRateLimitUnit {
  Seconds = 'SECONDS',
  Minutes = 'MINUTES',
  Hours = 'HOURS',
}

export interface BulkPublishingRateLimit {
  requests: number | null;
  interval: number | null;
  unit: BulkPublishingRateLimitUnit;
}

export interface BulkPublishingSettings {
  mode: BulkPublishingMode;
  rateLimit?: BulkPublishingRateLimit;
}

export interface IntegrationInstanceTypeDetails {
  type: string;
  parallelBulkPublishingSupported?: boolean;
}

export type IntegrationWithConfig = Omit<Outegration, 'redactedConfig'> & { config: OutegrationConfig };

export type Outegration = {
  id?: string;
  name: string;
  projectionName?: string;
  active: boolean;
  integrationType: string;
  category: string;
  redactedConfig: OutegrationConfig;
  redactedAuthConfig?: OutegrationConfig;
  schema?: SchemaField[];
  authenticationSourceId?: string;
  mapping: TMapping;
  matchingCondition?: {
    stringCondition?: any;
    fieldName?: string;
  } | null;
  targetMatchingType: string;
  gatewayId?: string;
  bulkPublishingSettings?: BulkPublishingSettings;
  childIntegrations?: Outegration[];
  inputSourceType?: string;
  staticSchemaType?: string;
};

export type ChildOutegrationConfig = {
  childOutegrationType: string;
  description?: string;
  displayConditions?: Record<string, string>;
  allowedProjections?: string[];
};

export type FieldMapping = {
  modelFieldName: null | string;
  fieldType: string;
  mappingType: MappingType;
  mappingValue: string;
  projName?: string;
  mandatory?: boolean;
  deleted?: boolean;
};

export const TARGET_MATCHING_CATEGORY_ENUM = {
  ALERTS: 'Alerts',
  AUTOMATION: 'Automation',
  SCANNER: 'Scanner',
  WORK_MANAGEMENT: 'Work Management',
  STORAGE: 'Storage',
  CMDB: 'CMDB',
  EXCEPTIONS: 'Exceptions',
  SIEM: 'SIEM',
};

export type TargetMatchingCategoryType = (typeof TARGET_MATCHING_CATEGORY_ENUM)[keyof typeof TARGET_MATCHING_CATEGORY_ENUM];

export enum TestType {
  Create = 'Create',
  Sync = 'Sync',
}

// Mirrors the server-side NotificationServices.BulkOutegrationMode proto enum.
export enum BulkOutegrationMode {
  Create = 'BULK_OUTEGRATION_MODE_CREATE',
  Sync = 'BULK_OUTEGRATION_MODE_SYNC',
}

export enum TestTypeKeys {
  Create = 'createMapping',
  Sync = 'syncMapping',
}

export type MappingGroup = {
  fieldMapping: FieldMapping[];
};

export enum IntegrationMappingType {
  CREATE = 'CREATE',
  SYNC = 'SYNC',
  WEBHOOK = 'WEBHOOK',
}

export type WorkflowType =
  | 'CreateIntegrationWorkflow'
  | 'IntegrationWebhookWorkflow'
  | 'SyncOutegrationWorkflow'
  | 'IntegrationAttachmentWorkflow'
  | 'AddCommentsWorkflow'
  | 'ConnectFabricEntityToExistingOutegrationWorkflow'
  | 'OutegrationUnlinkWorkflow'
  | 'BulkOutegrationWorkflow';

export interface WorkflowExecutionEntry {
  workflowsExecutions: {
    runId: string;
    workflowId: string;
    workflowType: WorkflowType;
    status: string;
    startTime: string;
    closeTime: string;
    searchAttributes?: Record<string, string>;
  }[];
  category: string;
  workflowMetadata: Record<string, unknown>;
  workflowMemo: Record<string, unknown>;
}

export interface FilteredWorkflowsResponse {
  executionMap: Record<string, WorkflowExecutionEntry>;
}

export const ProjectionsByCategory = {
  [TARGET_MATCHING_CATEGORY_ENUM.CMDB]: ['violation_tickets'],
  [TARGET_MATCHING_CATEGORY_ENUM.WORK_MANAGEMENT]: ['tickets', 'incidents', 'violation_tickets'],
  [TARGET_MATCHING_CATEGORY_ENUM.EXCEPTIONS]: ['uber_exceptions'],
  [TARGET_MATCHING_CATEGORY_ENUM.SCANNER]: ['uber_assets'],
  [TARGET_MATCHING_CATEGORY_ENUM.SIEM]: ['incidents'],
};

export type AvalorFailureCategory =
  | 'Unknown'
  | 'InvalidCredentials'
  | 'PermissionDenied'
  | 'ResourceNotFound'
  | 'Timeout'
  | 'RateLimitExceeded'
  | 'ServerError'
  | 'GatewayError'
  | 'ServiceUnavailable'
  | 'GatewayTimeout'
  | 'UnprocessableEntity'
  | 'Conflict'
  | 'DevelopersSourceCodeException'
  | 'TimeoutException'
  | 'SSLError'
  | 'BadRequest'
  | 'CertificateVerificationError'
  | 'NetworkError'
  | 'MissingSchema'
  | 'ParsingError'
  | 'ConfigValidation'
  | 'NoData'
  | 'ReadTimeout'
  | 'PreconditionFailed'
  | 'ConnectionReset'
  | 'HeartbeatTimeout'
  | 'ScheduleToCloseTimeout'
  | 'ScheduleToStartTimeout'
  | 'StartToCloseTimeout'
  | 'UnspecifiedTimeout'
  | 'UniqueConstraint'
  | 'PaymentRequiredException'
  | 'BadFilter'
  | 'PollingError'
  | 'InvalidValuesError'
  | 'VendorGatewayTimeout'
  | 'Transformation'
  | 'Loader'
  | 'Pyrunner'
  | 'NetworkIssue'
  | 'ZscalerConnectorInternalError'
  | 'DataVolumeError'
  | 'RateLimit'
  | 'GatewayProxy'
  | 'ApiDeprecation'
  | 'ResourceConflict'
  | 'UnknownError'
  | 'SSLIssue'
  | 'FileDoesNotExist'
  | 'UserScriptError'
  | 'ConfigurationError'
  | 'InternalError'
  | 'DataTypeMismatch'
  | 'NullEmptyValueViolation'
  | 'SourceFileError'
  | 'GeneralError';

export type InternalLink = {
  type?: 'DSI_MAPPING' | 'DATA_MODEL';
  params?: Record<string, string>;
};

export type InterpolatedMessage = {
  message: string;
  variables: { name: string; type: string }[];
};

export type FailureInfo = {
  category?: AvalorFailureCategory;
  description?: string;
  interpolatedDescription?: InterpolatedMessage;
  userMessage?: string;
  severity?: number;
  userFacing?: boolean;
  resolutionSteps?: string | InterpolatedMessage[];
  interpolatedResolutionSteps?: { resolutionSteps: InterpolatedMessage[] };
  stackTrace?: string;
  responseBody?: string;
  url?: string;
  retryable?: boolean;
  activityType?: string;
  workflowType?: WorkflowType;
  exceptionClass?: string;
  errorCode?: string;
  cause?: string;
  connectorName?: string;
  documentation?: string;
  curl?: string;
  vendorName?: string;
  internalLinks?: Record<string, InternalLink>;
  interpolatedFields?: Record<string, string>;
  details?: Record<string, string>;
  httpStatusCode?: number;
};
